/**
 * 将 AI 生成图注入 product-media 轮播，追加到末尾并切换。
 */
(function () {
  "use strict";

  let aiSlideCounter = 0;

  function findProductMedia(root) {
    const section = root?.closest(".shopify-section") || document;
    return (
      section.querySelector("product-media") ||
      document.querySelector("product-media")
    );
  }

  function getCarousel(productMedia) {
    return productMedia?.querySelector("flickity-carousel.product__media-list");
  }

  function parseCarouselIds(carousel) {
    const match = (carousel?.id || "").match(
      /^product-(.+)-(\d+)-media-list$/
    );
    if (!match) {
      return { sectionId: "unknown", productId: "0" };
    }
    return { sectionId: match[1], productId: match[2] };
  }

  function isFlickityEnabled(carousel) {
    return carousel?.classList.contains("flickity-enabled");
  }

  function getMaxOriginalPosition(carousel) {
    let max = -1;
    carousel.querySelectorAll(".product__media-item").forEach((el) => {
      const pos = parseInt(el.getAttribute("data-original-position"), 10);
      if (!Number.isNaN(pos) && pos > max) max = pos;
    });
    return max;
  }

  function findSlideByUrl(carousel, url) {
    const slides = carousel.querySelectorAll("[data-ai-image-url]");
    for (const slide of slides) {
      if (slide.getAttribute("data-ai-image-url") === url) return slide;
    }
    return null;
  }

  function getSlideImageUrl(slide) {
    return (
      slide.getAttribute("data-ai-image-url") ||
      slide.querySelector("img")?.getAttribute("src") ||
      slide.querySelector("img")?.currentSrc ||
      ""
    );
  }

  function loadImageDimensions(url) {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const width = img.naturalWidth || 1200;
        const height = img.naturalHeight || 1200;
        resolve({
          width,
          height,
          aspectRatio: width && height ? width / height : 1,
        });
      };
      img.onerror = () =>
        resolve({ width: 1200, height: 1200, aspectRatio: 1 });
      img.src = url;
    });
  }

  function getNativeSlideTemplate(carousel) {
    const refSlide = carousel.querySelector(
      '.product__media-item:not([data-ai-generated]):not(.is-filtered)'
    );
    const refWrapper = refSlide?.querySelector(".product__media-image-wrapper");
    const refImg = refSlide?.querySelector("img");

    const slideClasses = ["product__media-item"];
    if (refSlide?.classList.contains("product__media-item--peeking")) {
      slideClasses.push("product__media-item--peeking");
    }

    return {
      slideClasses,
      wrapperClasses:
        refWrapper?.className ||
        "product__media-image-wrapper aspect-ratio aspect-ratio--natural",
      imgClass: refImg?.className || "lozad",
    };
  }

  function createSlideElement({
    url,
    sectionId,
    position,
    aspectRatio,
    imgWidth,
    imgHeight,
    template,
  }) {
    const slideId = `ai-${Date.now()}-${++aiSlideCounter}`;
    const paddingBottom = (100 / aspectRatio).toFixed(4);
    const slide = document.createElement("div");
    slide.id = `product-${sectionId}-${slideId}`;
    slide.className = template.slideClasses.join(" ");
    slide.setAttribute("data-media-type", "image");
    slide.setAttribute("data-media-id", slideId);
    slide.setAttribute("data-ai-generated", "true");
    slide.setAttribute("data-ai-image-url", url);
    slide.setAttribute("data-aspect-ratio", String(aspectRatio));
    slide.setAttribute("data-original-position", String(position));
    slide.innerHTML = `
      <div class="${template.wrapperClasses}"
           style="padding-bottom: ${paddingBottom}%; --aspect-ratio: ${aspectRatio}">
        <img src="${url}" alt="AI Generated Design" class="${template.imgClass}" loading="lazy" width="${imgWidth}" height="${imgHeight}" />
      </div>
    `;
    return slide;
  }

  function createThumbnailButton({ url, slideId, slideDomId }) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.tabIndex = -1;
    btn.className = "product__thumbnail-item";
    btn.setAttribute("aria-controls", slideDomId);
    btn.setAttribute("data-media-id", slideId);
    btn.setAttribute("data-action", "select");
    btn.innerHTML = `
      <div class="product__thumbnail">
        <img src="${url}" alt="" loading="lazy" width="120" height="120" />
      </div>
    `;
    return btn;
  }

  function createDotButton({ slideDomId, slideId, index }) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.tabIndex = -1;
    btn.className = "dots-nav__item tap-area";
    btn.setAttribute("aria-controls", slideDomId);
    btn.setAttribute("data-media-id", slideId);
    btn.setAttribute("data-action", "select");
    btn.innerHTML = `<span class="visually-hidden">Slide ${index + 1}</span>`;
    return btn;
  }

  function ensureMediaNavShell(productMedia, carousel) {
    let controls = productMedia.querySelector("flickity-controls");
    const carouselId = carousel.id;

    if (!controls && carousel.querySelectorAll(".product__media-item:not(.is-filtered)").length > 1) {
      controls = document.createElement("flickity-controls");
      controls.className = "product__media-nav";
      controls.setAttribute("controls", carouselId);
      controls.innerHTML = `
        <button class="product__media-prev-next hidden-pocket hidden-lap-and-up tap-area tap-area--large"
                aria-label="Previous" data-action="prev"><span aria-hidden="true">&#8249;</span></button>
        <div class="dots-nav dots-nav--centered hidden-lap-and-up"></div>
        <scroll-shadow class="product__thumbnail-scroll-shadow hidden-pocket">
          <div class="product__thumbnail-list hide-scrollbar">
            <div class="product__thumbnail-list-inner"></div>
          </div>
        </scroll-shadow>
        <button class="product__media-prev-next hidden-pocket hidden-lap-and-up tap-area tap-area--large"
                aria-label="Next" data-action="next"><span aria-hidden="true">&#8250;</span></button>
      `;
      const wrapper = productMedia.querySelector(".product__media-list-wrapper");
      if (wrapper?.nextSibling) {
        productMedia.insertBefore(controls, wrapper.nextSibling);
      } else {
        productMedia.appendChild(controls);
      }
    }

    return controls;
  }

  function syncMediaNav(productMedia, carousel, activeSlideId) {
    const controls = ensureMediaNavShell(productMedia, carousel);
    if (!controls) return;

    const slides = Array.from(
      carousel.querySelectorAll(".product__media-item:not(.is-filtered)")
    );

    const thumbInner = controls.querySelector(".product__thumbnail-list-inner");
    const dotsNav = controls.querySelector(".dots-nav");

    if (thumbInner) {
      const existingIds = new Set(
        Array.from(thumbInner.querySelectorAll("[data-media-id]")).map((el) =>
          el.getAttribute("data-media-id")
        )
      );
      slides.forEach((slide) => {
        const slideId = slide.getAttribute("data-media-id");
        if (!slideId || existingIds.has(slideId)) return;
        const url = getSlideImageUrl(slide);
        if (!url) return;
        thumbInner.appendChild(
          createThumbnailButton({
            url,
            slideId,
            slideDomId: slide.id,
          })
        );
        existingIds.add(slideId);
      });
    }

    if (dotsNav) {
      const existingDotIds = new Set(
        Array.from(dotsNav.querySelectorAll("[data-media-id]")).map((el) =>
          el.getAttribute("data-media-id")
        )
      );
      slides.forEach((slide, index) => {
        const slideId = slide.getAttribute("data-media-id");
        if (!slideId || existingDotIds.has(slideId)) return;
        dotsNav.appendChild(
          createDotButton({
            slideDomId: slide.id,
            slideId,
            index,
          })
        );
        existingDotIds.add(slideId);
      });
    }

    controls.hidden = false;
    controls.style.display = "";

    if (activeSlideId) {
      controls.querySelectorAll("[data-media-id]").forEach((btn) => {
        const isCurrent = btn.getAttribute("data-media-id") === activeSlideId;
        if (isCurrent) {
          btn.setAttribute("aria-current", "true");
        } else {
          btn.removeAttribute("aria-current");
        }
      });
    }
  }

  async function getFlickityInstance(carousel) {
    try {
      return await carousel.flickityInstance;
    } catch {
      return null;
    }
  }

  async function appendSlideToCarousel(carousel, slide) {
    if (isFlickityEnabled(carousel)) {
      const flickity = await getFlickityInstance(carousel);
      if (flickity && typeof flickity.append === "function") {
        flickity.append(slide);
        return;
      }
      const slider = carousel.querySelector(".flickity-slider");
      if (slider) {
        slider.appendChild(slide);
        return;
      }
    }
    carousel.appendChild(slide);
  }

  async function initOrRefreshCarousel(carousel, slideToSelect) {
    const slideId = slideToSelect?.getAttribute("data-media-id");

    if (isFlickityEnabled(carousel)) {
      const flickity = await getFlickityInstance(carousel);
      if (flickity) {
        if (typeof flickity.reloadCells === "function") {
          flickity.reloadCells();
        } else {
          await carousel.reload();
          const refreshed = await getFlickityInstance(carousel);
          if (refreshed && slideToSelect) {
            refreshed.selectCell(slideToSelect);
            refreshed.resize();
          }
          return refreshed;
        }
        if (slideToSelect) {
          flickity.selectCell(slideToSelect);
          flickity.resize();
        }
        return flickity;
      }
    }

    const visibleSlides = carousel.querySelectorAll(
      ".product__media-item:not(.is-filtered)"
    );
    if (visibleSlides.length <= 1) {
      return null;
    }

    // 单图商品首次追加：主题 constructor 因 childElementCount===1 跳过了 Flickity 初始化。
    // 在启用 Flickity 前不要移除原图的 is-selected，否则非 flickity 状态下原图会被 CSS 隐藏。
    carousel.querySelectorAll(".product__media-item").forEach((item) => {
      item.classList.remove("is-initial-selected");
    });
    if (slideToSelect) {
      slideToSelect.classList.add("is-initial-selected", "is-selected");
    }

    if (typeof carousel._createFlickity === "function") {
      await carousel._createFlickity();
    }

    const flickity = await getFlickityInstance(carousel);
    if (flickity && slideToSelect) {
      flickity.selectCell(slideToSelect);
      flickity.resize();
    }
    return flickity;
  }

  async function selectExistingSlide(productMedia, carousel, slide) {
    syncMediaNav(
      productMedia,
      carousel,
      slide.getAttribute("data-media-id")
    );

    if (isFlickityEnabled(carousel)) {
      const flickity = await getFlickityInstance(carousel);
      if (flickity) {
        flickity.selectCell(slide);
        flickity.resize();
        return slide;
      }
    }

    carousel.querySelectorAll(".product__media-item").forEach((item) => {
      item.classList.remove("is-selected", "is-initial-selected");
    });
    slide.classList.add("is-selected", "is-initial-selected");
    return slide;
  }

  async function addImageToProductMedia(imageUrl, options = {}) {
    const { select = true, root = document } = options;
    if (!imageUrl) return null;

    const productMedia = findProductMedia(root);
    const carousel = getCarousel(productMedia);
    if (!productMedia || !carousel) {
      console.warn("[AiProductMedia] product-media not found");
      return null;
    }

    const existing = findSlideByUrl(carousel, imageUrl);
    if (existing) {
      if (select) {
        await selectExistingSlide(productMedia, carousel, existing);
      }
      return existing;
    }

    const { sectionId } = parseCarouselIds(carousel);
    const position = getMaxOriginalPosition(carousel) + 1;
    const template = getNativeSlideTemplate(carousel);
    const dimensions = await loadImageDimensions(imageUrl);

    const slide = createSlideElement({
      url: imageUrl,
      sectionId,
      position,
      aspectRatio: dimensions.aspectRatio,
      imgWidth: dimensions.width,
      imgHeight: dimensions.height,
      template,
    });

    await appendSlideToCarousel(carousel, slide);

    syncMediaNav(
      productMedia,
      carousel,
      select ? slide.getAttribute("data-media-id") : null
    );

    if (select) {
      await initOrRefreshCarousel(carousel, slide);
      syncMediaNav(
        productMedia,
        carousel,
        slide.getAttribute("data-media-id")
      );
    } else if (isFlickityEnabled(carousel)) {
      const flickity = await getFlickityInstance(carousel);
      flickity?.reloadCells?.();
      flickity?.resize?.();
    }

    window.dispatchEvent(new Event("resize"));
    return slide;
  }

  function buildAiPhotoSwipeItem(item) {
    const img = item.querySelector("img");
    const src =
      item.getAttribute("data-ai-image-url") ||
      img?.currentSrc ||
      img?.src ||
      "";
    const aspectRatio =
      parseFloat(item.getAttribute("data-aspect-ratio")) ||
      (img?.naturalWidth && img?.naturalHeight
        ? img.naturalWidth / img.naturalHeight
        : 1);
    const sourceWidth = img?.naturalWidth || 1600;
    const desiredWidth = Math.min(sourceWidth, window.innerWidth);

    return {
      selected: item.classList.contains("is-selected"),
      src,
      msrc: img?.currentSrc || src,
      originalMedia: null,
      w: desiredWidth,
      h: Math.max(1, Math.round(desiredWidth / aspectRatio)),
    };
  }

  function patchProductImageZoom() {
    const ctor = customElements.get("product-image-zoom");
    if (!ctor?.prototype?._buildItems || ctor.prototype._aiZoomPatched) {
      return;
    }

    const originalBuildItems = ctor.prototype._buildItems;

    ctor.prototype._buildItems = async function () {
      const activeImages = Array.from(
        this.mediaElement.querySelectorAll(
          '.product__media-item[data-media-type="image"]:not(.is-filtered)'
        )
      );
      const aiSlides = activeImages.filter((item) =>
        item.hasAttribute("data-ai-generated")
      );

      aiSlides.forEach((slide) => slide.classList.add("is-filtered"));

      let shopifyItems = [];
      try {
        shopifyItems = await originalBuildItems.call(this);
      } finally {
        aiSlides.forEach((slide) => slide.classList.remove("is-filtered"));
      }

      let shopifyIndex = 0;
      return activeImages.map((item) => {
        if (item.hasAttribute("data-ai-generated")) {
          return buildAiPhotoSwipeItem(item);
        }
        const shopifyItem = shopifyItems[shopifyIndex++];
        if (!shopifyItem) {
          return buildAiPhotoSwipeItem(item);
        }
        return shopifyItem;
      });
    };

    ctor.prototype._aiZoomPatched = true;
  }

  function initProductImageZoomPatch() {
    if (customElements.get("product-image-zoom")) {
      patchProductImageZoom();
      return;
    }
    customElements.whenDefined("product-image-zoom").then(patchProductImageZoom);
  }

  initProductImageZoomPatch();

  function isMobileViewport() {
    return window.matchMedia("(max-width: 999px)").matches;
  }

  function getScrollTopOffset() {
    const styles = getComputedStyle(document.documentElement);
    const header = parseFloat(styles.getPropertyValue("--header-height"));
    const announcement = parseFloat(
      styles.getPropertyValue("--announcement-bar-height")
    );
    const offset =
      (Number.isFinite(header) ? header : 0) +
      (Number.isFinite(announcement) ? announcement : 0) +
      16;
    return offset;
  }

  function scrollToProductMedia(root) {
    if (!isMobileViewport()) return;

    const productMedia = findProductMedia(root);
    if (!productMedia) return;

    const drawer = root.closest("quick-buy-drawer");
    const drawerContent =
      drawer?.querySelector(".drawer__content") ||
      drawer?.querySelector(".drawer__scrollable");

    const runScroll = () => {
      if (drawerContent) {
        const containerRect = drawerContent.getBoundingClientRect();
        const mediaRect = productMedia.getBoundingClientRect();
        const nextTop =
          drawerContent.scrollTop + (mediaRect.top - containerRect.top) - 12;
        drawerContent.scrollTo({
          top: Math.max(0, nextTop),
          behavior: "smooth",
        });
        return;
      }

      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop || 0;
      const targetY =
        productMedia.getBoundingClientRect().top +
        scrollTop -
        getScrollTopOffset();

      window.scrollTo({
        top: Math.max(0, targetY),
        behavior: "smooth",
      });
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(runScroll);
    });
  }

  window.AiProductMedia = {
    addImageToProductMedia,
    findProductMedia,
    scrollToProductMedia,
  };
})();
